import { ChangeDetectionStrategy, Component, effect, inject, input, signal, untracked } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription, forkJoin } from 'rxjs';

import { AccessPermissionType } from 'src/app/bo/accessPermisionType';
import { ConfigurationSaisieDonneesAnalyse, ModeSaisie } from 'src/app/bo/configurationSaisieDonneesAnalyse';
import { DonneeAnalyse } from 'src/app/bo/donneeAnalyse';
import { Projet } from 'src/app/bo/projet';
import { BlastKpiComponent, TypeParent } from 'src/app/features/blast/blast-kpi/blast-kpi.component';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { KpiService } from 'src/app/services/kpi.service';
import { ItemSortHelper } from 'src/app/utils/itemSortHelper';

const MODE_SAISIE_KEYS: Partial<Record<ModeSaisie, string>> = {
  [ModeSaisie.Manuelle]: 'GLOBAL.MANUELLE',
  [ModeSaisie.Max]:      'GLOBAL.MAXIMUM',
  [ModeSaisie.Min]:      'GLOBAL.MINIMUM',
  [ModeSaisie.Somme]:    'GLOBAL.SOMME',
  [ModeSaisie.Moyenne]:  'GLOBAL.MOYENNE',
};

@Component({
  selector:        'app-site-analysis-data',
  standalone:      true,
  template: `
    <app-blast-kpi
      [tabConfigSaisie]="configSaisie()"
      [tabDonneesAnalyse]="donnees()"
      [donneesChargees]="loaded()"
      [estProjet]="!!project()"
      [typeParent]="project() ? undefined : TypeParent.Noeud"
      [projet]="project()"
      [inProjetDetail]="true"
      (rechargerDonnees)="reload()"
      class="w-100" />
  `,
  styles: [':host { display: flex; flex-direction: column; flex: 1; min-height: 0; overflow: hidden; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BlastKpiComponent],
})
export class SiteAnalysisDataComponent {
  readonly siteId   = input.required<number | null>();
  readonly project  = input<Projet | null>(null);
  readonly blastIds = input<number[]>([]);

  private readonly kpiService       = inject(KpiService);
  private readonly fonctionsService = inject(FonctionsService);
  private readonly translate        = inject(TranslateService);
  private readonly userService      = inject(KeycloakUtilisateurService);

  readonly TypeParent = TypeParent;

  readonly donnees      = signal<DonneeAnalyse[]>([]);
  readonly configSaisie = signal<ConfigurationSaisieDonneesAnalyse[]>([]);
  readonly loaded       = signal(false);

  private request?: Subscription;

  constructor() {
    effect(() => {
      const siteId    = this.siteId();
      const projectId = this.project()?.projetId ?? null;
      const blastIds  = this.blastIds();
      untracked(() => this.load(siteId, projectId, blastIds));
    });
  }

  /** Recharge après une modification faite dans app-blast-kpi. */
  reload(): void {
    this.load(this.siteId(), this.project()?.projetId ?? null, this.blastIds());
  }

  /**
   * Repris de l'ancien front : projet-detail (données du projet, saisie manuelle selon la config)
   * et noeud-detail (agrégat des tirs du nœud, lecture seule).
   */
  private load(siteId: number | null, projectId: number | null, blastIds: number[]): void {
    this.request?.unsubscribe();
    this.loaded.set(false);
    this.donnees.set([]);
    this.configSaisie.set([]);
    if (!siteId || (!projectId && !blastIds.length)) return;

    const donnees$ = projectId
      ? this.kpiService.getProjectAnalysisData(projectId)
      : this.kpiService.getBlaststAnalysisData(blastIds.join('&blastsId='));
    const isGuest = !this.userService.allRoles.includes('access_all_kpi');

    const startLoader = setTimeout(() => this.fonctionsService.loading_screen(true), 300);
    const stopLoader  = () => { clearTimeout(startLoader); this.fonctionsService.loading_screen(false); };

    this.request = forkJoin([donnees$, this.kpiService.getSiteConfigAnalysisEntry(siteId)]).subscribe({
      next: async ([donnees, configs]) => {
        donnees.forEach(donnee => {
          const config = configs.find(c => c.referentielDonneeAnalyseId === donnee.referentielDonneeAnalyseId);
          if (!config) return;
          donnee.isEnabled        = config.isEnabled;
          donnee.accessPermission = config.accessPermission;
          donnee.autorisePourUtil = !!projectId
            && ((isGuest && config.accessPermission === AccessPermissionType.READANDWRITE) || (!isGuest && config.isEnabled))
            && config.modeSaisieDonneeProjet === ModeSaisie.Manuelle;
          const modeKey = MODE_SAISIE_KEYS[config.modeSaisieDonneeProjet];
          if (modeKey) donnee.modeSaisie = this.translate.instant(modeKey);
        });

        if (projectId) {
          donnees.sort((a, b) => a.autorisePourUtil === b.autorisePourUtil
            ? a.referentielDonneeAnalyse.nom.localeCompare(b.referentielDonneeAnalyse.nom)
            : a.autorisePourUtil ? -1 : 1);
        }
        try { donnees = await ItemSortHelper.sortKpi(donnees); } catch { }

        stopLoader();
        this.donnees.set(donnees);
        this.configSaisie.set(configs);
        this.loaded.set(true);
      },
      error: () => stopLoader(),
    });
  }
}
